import {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {QUERY_CACHE_KEYS, type InsuranceSetup} from '@chekin/core';
import {API} from '@chekin/api';
import {useEvent} from '@chekinapp/ui';
import {usePaymentsEnv} from '../PaymentsEnv';

const CACHE_TIME = 60_000;
const STALE_TIME = 60_000;

/**
 * The reservation's protection choice — the cart needs it to know whether
 * the deposit/protection item starts in "pay later". Shares its query key
 * with the guestapp's deposit screen so both read one cache entry.
 */
function useInsuranceSetup({onError}: {onError?: (error: Error) => void} = {}) {
  const {isTokenValid, reservationId, insuranceSetupId} = usePaymentsEnv();
  const handleError = useEvent(onError);

  const {
    data: insuranceSetup,
    error,
    isSuccess,
  } = useQuery<InsuranceSetup>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.insuranceSetup],
    queryFn: () => API.propertyProtection.getInsuranceSetup(insuranceSetupId || ''),
    enabled: Boolean(insuranceSetupId && isTokenValid),
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });

  useEffect(() => {
    if (error) handleError(error);
  }, [error, handleError]);

  return {
    insuranceSetup,
    isInsuranceSetupInit: insuranceSetupId ? isSuccess : Boolean(reservationId),
  };
}

export {useInsuranceSetup};
